import React from 'react';
import { Link } from 'react-router-dom';
import { LEGAL_EFFECTIVE_DATE, LEGAL_CONTACT_EMAIL } from '../../config/legal';

// Starter Terms of Service. Plain-language and deliberately modest in scope —
// enough to launch responsibly. To be reviewed by counsel once traffic warrants
// it. Keep the version/date in sync with src/config/legal.ts.
export const Terms: React.FC = () => {
  return (
    <div className="text-white max-w-app-middle-side mx-auto w-full px-app-base py-app-xl leading-relaxed">
      <h1 className="text-app-3xl font-bold">Terms of Service</h1>
      <p className="text-app-sm text-app-gray mt-app-xs">Effective {LEGAL_EFFECTIVE_DATE}</p>

      <p className="text-app-gray-light mt-app-lg">
        Welcome to <span className="text-app-primary font-semibold">Pinion</span>. These Terms of
        Service (&ldquo;Terms&rdquo;) govern your use of the Pinion website and services (the
        &ldquo;Service&rdquo;). By creating an account or using the Service, you agree to these Terms
        and to our{' '}
        <Link to="/privacy" className="text-app-primary underline">Privacy Policy</Link>. If you do
        not agree, please do not use the Service.
      </p>

      <h2 className="text-app-xl font-bold mt-app-2xl">1. Who can use Pinion</h2>
      <p className="text-app-gray-light mt-app-base">
        You must be at least 13 years old to use Pinion. If you are under the age of majority where
        you live, you may only use the Service with the involvement of a parent or guardian. By using
        Pinion you confirm you meet these requirements and that the information you provide is
        accurate.
      </p>

      <h2 className="text-app-xl font-bold mt-app-2xl">2. Your account</h2>
      <p className="text-app-gray-light mt-app-base">
        You are responsible for keeping your login credentials secure and for all activity under your
        account. Tell us promptly if you believe your account has been compromised. You may close your
        account at any time.
      </p>

      <h2 className="text-app-xl font-bold mt-app-2xl">3. What Pinion is</h2>
      <p className="text-app-gray-light mt-app-base">
        Pinion lets you post short Yes/No questions (&ldquo;Pinions&rdquo;), vote on them, comment, and
        discover people whose answers line up with yours. Votes and aggregate results are public by
        design.
      </p>

      <h2 className="text-app-xl font-bold mt-app-2xl">4. Your content</h2>
      <p className="text-app-gray-light mt-app-base">
        You keep ownership of the Pinions, comments, and other content you post. By posting, you grant
        Pinion a worldwide, non-exclusive, royalty-free licence to host, store, display, and
        distribute that content for the purpose of operating and promoting the Service. You are
        responsible for what you post and confirm you have the right to post it.
      </p>

      <h2 className="text-app-xl font-bold mt-app-2xl">5. Acceptable use</h2>
      <p className="text-app-gray-light mt-app-base">You agree not to:</p>
      <ul className="mt-app-base flex flex-col gap-app-base list-disc pl-app-lg text-app-gray-light">
        <li>Post content that is illegal, hateful, harassing, threatening, or incites violence.</li>
        <li>Harass, bully, impersonate, or infringe the rights of others.</li>
        <li>Post spam, scams, malware, or sexually explicit or exploitative material.</li>
        <li>Attempt to disrupt, overload, reverse-engineer, or gain unauthorised access to the Service.</li>
        <li>Manipulate votes, results, or rankings through automated or deceptive means.</li>
      </ul>

      <h2 className="text-app-xl font-bold mt-app-2xl">6. Moderation and termination</h2>
      <p className="text-app-gray-light mt-app-base">
        We may remove content or suspend or terminate accounts that breach these Terms or that we
        reasonably believe harm other users or the Service. Where practical we will give notice, but we
        may act immediately in serious cases.
      </p>

      <h2 className="text-app-xl font-bold mt-app-2xl">7. Intellectual property</h2>
      <p className="text-app-gray-light mt-app-base">
        The Pinion name, logo, and software are owned by us or our licensors. These Terms do not grant
        you any rights to our branding except as needed to use the Service normally.
      </p>

      <h2 className="text-app-xl font-bold mt-app-2xl">8. Disclaimers</h2>
      <p className="text-app-gray-light mt-app-base">
        The Service is provided &ldquo;as is&rdquo; and &ldquo;as available&rdquo; without warranties
        of any kind. Opinions expressed on Pinion belong to their authors, not to us. We do not
        guarantee the Service will be uninterrupted, secure, or error-free.
      </p>

      <h2 className="text-app-xl font-bold mt-app-2xl">9. Limitation of liability</h2>
      <p className="text-app-gray-light mt-app-base">
        To the fullest extent permitted by law, Pinion will not be liable for any indirect, incidental,
        or consequential damages, or for loss of data, profits, or goodwill, arising from your use of
        the Service.
      </p>

      <h2 className="text-app-xl font-bold mt-app-2xl">10. Changes to these Terms</h2>
      <p className="text-app-gray-light mt-app-base">
        We may update these Terms from time to time. If we make material changes we will update the
        effective date above and, where appropriate, ask you to review and accept the new version. Your
        continued use after changes take effect means you accept them.
      </p>

      <h2 className="text-app-xl font-bold mt-app-2xl">11. Governing law</h2>
      <p className="text-app-gray-light mt-app-base">
        These Terms are governed by the laws of Sweden, without regard to its conflict-of-laws rules.
        Nothing here removes mandatory consumer protections you may have where you live.
      </p>

      <h2 className="text-app-xl font-bold mt-app-2xl">12. Contact</h2>
      <p className="text-app-gray-light mt-app-base">
        Questions about these Terms? Reach us at{' '}
        <a href={`mailto:${LEGAL_CONTACT_EMAIL}`} className="text-app-primary underline">
          {LEGAL_CONTACT_EMAIL}
        </a>
        .
      </p>

      <p className="text-app-gray-light mt-app-2xl">
        See also our{' '}
        <Link to="/privacy" className="text-app-primary underline">Privacy Policy</Link>.
      </p>
    </div>
  );
};
